import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Package, ShoppingBag, Ellipsis, ChevronRight, Users, X, Search } from 'lucide-react';
import Menu from '../components/Menu';
import SearchBar from '../components/SearchBar';
import CampanaNotificaciones from '../components/Landing/Notificaciones/CampanaNotificaciones';
import { getMobileNav, groupMoreItems, isPathActive } from './mobileNav';
import DownloadApps from './DownloadApps';
import './MobileLayout.css';
import './mobile-screens.css';

const TAB_ICONS = {
    '/': Home,
    '/Publicaciones': Package,
    '/Ordenes': ShoppingBag,
    '/Sellers': Users,
};

function getTabIcon(path) {
    return TAB_ICONS[path] || Home;
}

function MobileLayout({ children, userType, idSeller, idUser, hiddenItemsId }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [moreOpen, setMoreOpen] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [sheetOffset, setSheetOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStartY = useRef(0);
    const dragCurrentY = useRef(0);

    const nav = useMemo(
        () => getMobileNav(userType, { idSeller, idUser, hiddenItemsId }),
        [userType, idSeller, idUser, hiddenItemsId]
    );

    const moreGroups = useMemo(() => groupMoreItems(nav.moreItems), [nav.moreItems]);
    const moreIsActive = nav.moreItems.some((item) => isPathActive(location.pathname, item.path));
    const isSeller = nav.role === 'seller';

    useEffect(() => {
        setMoreOpen(false);
        setSearchOpen(false);
        setSheetOffset(0);
        setDragging(false);
    }, [location.pathname]);

    const closeMore = () => {
        setMoreOpen(false);
        setOpenSection(null);
        setSheetOffset(0);
        setDragging(false);
    };

    const openMore = () => {
        const activeGroup = moreGroups.find((group) =>
            group.items.some((item) => isPathActive(location.pathname, item.path))
        );
        setOpenSection(activeGroup ? activeGroup.section : null);
        setMoreOpen(true);
    };

    const toggleSection = (section) => {
        setOpenSection((current) => (current === section ? null : section));
    };

    const goTo = (path) => {
        closeMore();
        if (path && path !== location.pathname) {
            navigate(path);
        }
    };

    const onDragStart = (clientY) => {
        dragStartY.current = clientY;
        dragCurrentY.current = clientY;
        setDragging(true);
    };

    const onDragMove = (clientY) => {
        if (!dragging) return;
        dragCurrentY.current = clientY;
        const delta = Math.max(0, clientY - dragStartY.current);
        setSheetOffset(delta);
    };

    const onDragEnd = () => {
        if (!dragging) return;
        const delta = dragCurrentY.current - dragStartY.current;
        setDragging(false);
        if (delta > 90) {
            closeMore();
            return;
        }
        setSheetOffset(0);
    };

    return (
        <div className={`mobile-layout${isSeller ? ' mobile-layout--seller' : ''}`}>
            <header className="mobile-header">
                <img
                    className="mobile-header__logo"
                    src="/img/mini-logo-marketplace-megatone.png"
                    alt="Marketplace"
                />
                <span className="mobile-header__role">{nav.roleLabel}</span>
                <div className="mobile-header__actions">
                    <DownloadApps compact />
                    <button
                        type="button"
                        className={`mobile-header__icon-btn${searchOpen ? ' is-active' : ''}`}
                        onClick={() => setSearchOpen((open) => !open)}
                        aria-label={searchOpen ? 'Cerrar búsqueda' : 'Buscar'}
                        aria-expanded={searchOpen}
                    >
                        <Search size={20} />
                    </button>
                    <CampanaNotificaciones />
                    <Menu compact />
                </div>
                {searchOpen && (
                    <div className="mobile-header__search">
                        <SearchBar idSeller={idSeller} listenClear />
                    </div>
                )}
            </header>

            <main className="mobile-content">
                {children}
            </main>

            <nav className="mobile-bottom-nav" aria-label="Navegación móvil">
                {nav.tabs.map((item) => {
                    const Icon = getTabIcon(item.path);
                    const active = isPathActive(location.pathname, item.path);
                    return (
                        <button
                            key={item.id}
                            type="button"
                            className={`mobile-bottom-nav__item${active ? ' is-active' : ''}`}
                            onClick={() => goTo(item.path)}
                        >
                            <Icon size={20} strokeWidth={active ? 2.4 : 2} />
                            <span>{item.label}</span>
                        </button>
                    );
                })}
                {nav.moreItems.length > 0 && (
                    <button
                        type="button"
                        className={`mobile-bottom-nav__item${moreIsActive || moreOpen ? ' is-active' : ''}`}
                        onClick={openMore}
                    >
                        <Ellipsis size={20} strokeWidth={moreIsActive || moreOpen ? 2.4 : 2} />
                        <span>Más</span>
                    </button>
                )}
            </nav>

            {moreOpen && (
                <div
                    className="mobile-more-backdrop"
                    onClick={closeMore}
                    role="presentation"
                >
                    <div
                        className={`mobile-more-sheet${dragging ? ' is-dragging' : ''}${isSeller ? ' mobile-more-sheet--seller' : ''}`}
                        style={{ transform: `translateY(${sheetOffset}px)` }}
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-label="Más secciones"
                    >
                        <div
                            className="mobile-more-sheet__grab"
                            onTouchStart={(e) => onDragStart(e.touches[0].clientY)}
                            onTouchMove={(e) => onDragMove(e.touches[0].clientY)}
                            onTouchEnd={onDragEnd}
                            onMouseDown={(e) => onDragStart(e.clientY)}
                            onMouseMove={(e) => {
                                if (e.buttons === 1) onDragMove(e.clientY);
                            }}
                            onMouseUp={onDragEnd}
                            onMouseLeave={() => {
                                if (dragging) onDragEnd();
                            }}
                        >
                            <div className="mobile-more-sheet__handle" />
                            <div className="mobile-more-sheet__header">
                                <h2 className="mobile-more-sheet__title">Más secciones</h2>
                                <button
                                    type="button"
                                    className="mobile-more-sheet__close"
                                    onClick={closeMore}
                                    aria-label="Cerrar menú"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <p className="mobile-more-sheet__hint">Deslizá hacia abajo para volver</p>
                        </div>

                        <div className="mobile-more-sheet__body">
                            {moreGroups.map((group) => {
                                const sectionOpen = openSection === group.section;
                                return (
                                    <div key={group.section} className="mobile-more-sheet__group">
                                        <button
                                            type="button"
                                            className={`mobile-more-sheet__section${sectionOpen ? ' is-open' : ''}`}
                                            onClick={() => toggleSection(group.section)}
                                            aria-expanded={sectionOpen}
                                        >
                                            <span>{group.section}</span>
                                            <ChevronRight size={16} />
                                        </button>
                                        {sectionOpen && group.items.map((item) => {
                                            const active = isPathActive(location.pathname, item.path);
                                            return (
                                                <button
                                                    key={`${group.section}-${item.id}`}
                                                    type="button"
                                                    className={`mobile-more-sheet__item${active ? ' is-active' : ''}`}
                                                    onClick={() => goTo(item.path)}
                                                >
                                                    <span>{item.label}</span>
                                                    <ChevronRight size={16} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    userType: state.general?.userType,
    idSeller: state.general?.idSeller,
    idUser: state.general?.userId,
    hiddenItemsId: state.sidebar?.hiddenItemsId,
});

export default connect(mapStateToProps)(MobileLayout);
